import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/firebase';
import { 
  getAllBadges, 
  getCurrentBadges, 
  awardMultipleBadges, 
  removeMultipleBadges,
  searchUsers,
  getBadgeAuditLog
} from '../utils/badgeManagement';
import Header from '../components/Header';

const AdminBadgeManagement = () => {
  const navigate = useNavigate();
  const { user: currentUser, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('award');
  const [userType, setUserType] = useState('team');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [currentBadges, setCurrentBadges] = useState([]);
  const [availableBadges, setAvailableBadges] = useState([]);
  const [selectedBadges, setSelectedBadges] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [auditLog, setAuditLog] = useState([]);

  // Check if user is admin and redirect if not
  useEffect(() => {
    if (!loading && (!currentUser || currentUser.role !== 'admin')) {
      navigate('/login');
    }
  }, [currentUser, loading, navigate]);

  const loadBadges = useCallback(() => {
    try {
      const { allBadges } = getAllBadges(userType);
      setAvailableBadges(allBadges);
    } catch (error) {
      setError('Error loading badges: ' + error.message);
    }
  }, [userType]);

  useEffect(() => {
    if (currentUser && currentUser.role === 'admin') {
      loadBadges();
    }
  }, [userType, currentUser, loadBadges]);

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setSearchResults([]);
      return;
    }

    try {
      setError(''); // Clear previous errors
      console.log(`Searching for ${userType}s with term: "${searchTerm}"`);
      
      const results = await searchUsers(searchTerm, userType);
      console.log(`Search results:`, results);
      
      setSearchResults(results);
      
      if (results.length === 0) {
        setError(`No ${userType}s found matching "${searchTerm}". Try a different search term.`);
      }
    } catch (error) {
      console.error('Search error:', error);
      setError('Error searching users: ' + error.message);
    }
  };

  const handleUserSelect = async (user) => {
    setSelectedUser(user);
    setSelectedBadges([]);
    
    try {
      const badges = await getCurrentBadges(user.id, userType);
      setCurrentBadges(badges);
      
      // Load audit log
      const log = await getBadgeAuditLog(user.id, userType);
      setAuditLog(log);
    } catch (error) {
      setError('Error loading user badges: ' + error.message);
    }
  };

  const handleAwardBadge = async () => {
    if (!selectedUser || selectedBadges.length === 0) {
      setError('Please select a user and at least one badge');
      return;
    }

    setActionLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await awardMultipleBadges(
        selectedUser.id, 
        userType, 
        selectedBadges, 
        currentUser.uid
      );

      if (result.success) {
        setMessage(result.message);
        // Refresh current badges
        const badges = await getCurrentBadges(selectedUser.id, userType);
        setCurrentBadges(badges);
        setSelectedBadges([]);
        
        // Refresh audit log
        const log = await getBadgeAuditLog(selectedUser.id, userType);
        setAuditLog(log);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Error awarding badges: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRemoveBadge = async () => {
    if (!selectedUser || selectedBadges.length === 0) {
      setError('Please select a user and at least one badge');
      return;
    }

    setActionLoading(true);
    setError('');
    setMessage('');

    try {
      const result = await removeMultipleBadges(
        selectedUser.id, 
        userType, 
        selectedBadges, 
        currentUser.uid
      );

      if (result.success) {
        setMessage(result.message);
        // Refresh current badges
        const badges = await getCurrentBadges(selectedUser.id, userType);
        setCurrentBadges(badges);
        setSelectedBadges([]);
        
        // Refresh audit log
        const log = await getBadgeAuditLog(selectedUser.id, userType);
        setAuditLog(log);
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError('Error removing badges: ' + error.message);
    } finally {
      setActionLoading(false);
    }
  };

  const toggleBadgeSelection = (badgeId) => {
    setSelectedBadges(prev => 
      prev.includes(badgeId) 
        ? prev.filter(id => id !== badgeId)
        : [...prev, badgeId]
    );
  };

  const getDisplayName = (user) => {
    if (userType === 'team') {
      return user.name || 'Unnamed Team';
    } else if (userType === 'host') {
      return user.displayName || user.email || 'Unknown Host';
    } else if (userType === 'venue') {
      return user.name || 'Unnamed Venue';
    }
    return 'Unknown';
  };

  const getBadgeInfo = (badgeId) => {
    const { getInfoFunction } = getAllBadges(userType);
    return getInfoFunction(badgeId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900">
        <Header />
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-white text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-white mb-2">🏆 Badge Management</h1>
            <p className="text-gray-400">Award and remove badges for hosts, teams, and venues</p>
          </div>

          {/* User Type Selector */}
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-white mb-4">Select User Type</h2>
            <div className="flex space-x-4">
              {['team', 'host', 'venue'].map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    setUserType(type);
                    setSelectedUser(null);
                    setSearchResults([]);
                    setSearchTerm('');
                  }}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                    userType === type
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}s
                </button>
              ))}
            </div>
          </div>

          {/* Search Section */}
          <div className="bg-gray-800 rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-white mb-4">Search {userType.charAt(0).toUpperCase() + userType.slice(1)}s</h2>
            
            {/* Search Instructions */}
            <div className="mb-4 p-3 bg-gray-700 rounded-lg">
              <p className="text-gray-300 text-sm">
                {userType === 'host' && '💡 Search by email address (e.g., "john@example.com") or display name'}
                {userType === 'team' && '💡 Search by team name, league ID (e.g., "T001"), captain email, or home venue'}
                {userType === 'venue' && '💡 Search by venue name (e.g., "Downtown Bar")'}
              </p>
            </div>
            
            <div className="flex space-x-4">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={
                  userType === 'host' ? 'Enter email or name (e.g., "john@example.com")' :
                  userType === 'team' ? 'Enter team name, league ID, captain email, or venue' :
                  'Enter venue name'
                }
                className="flex-1 p-3 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-green-500 focus:outline-none"
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <button
                onClick={handleSearch}
                disabled={!searchTerm.trim()}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg font-medium transition-colors"
              >
                Search
              </button>
            </div>

            {/* Search Results */}
            {searchResults.length > 0 && (
              <div className="mt-4">
                <h3 className="text-lg font-medium text-white mb-3">Search Results ({searchResults.length})</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {searchResults.map((user) => (
                    <div
                      key={user.id}
                      onClick={() => handleUserSelect(user)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-colors ${
                        selectedUser?.id === user.id
                          ? 'border-green-500 bg-green-900/20'
                          : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                      }`}
                    >
                      <h4 className="text-white font-medium">{getDisplayName(user)}</h4>
                      {userType === 'host' && user.email && (
                        <p className="text-gray-400 text-sm">{user.email}</p>
                      )}
                      {userType === 'host' && user.role && (
                        <p className="text-gray-400 text-sm">Role: {user.role}</p>
                      )}
                      {userType === 'team' && user.leagueId && (
                        <p className="text-gray-400 text-sm">League ID: #{user.leagueId}</p>
                      )}
                      {userType === 'team' && user.captainEmail && (
                        <p className="text-gray-400 text-sm">Captain: {user.captainEmail}</p>
                      )}
                      {userType === 'team' && user.homeVenue && (
                        <p className="text-gray-400 text-sm">Venue: {user.homeVenue}</p>
                      )}
                      {userType === 'venue' && user.address && (
                        <p className="text-gray-400 text-sm">{user.address}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Badge Management Section */}
          {selectedUser && (
            <div className="bg-gray-800 rounded-lg p-6 mb-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-white">
                  Manage Badges for: {getDisplayName(selectedUser)}
                </h2>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setActiveTab('award')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeTab === 'award'
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Award Badges
                  </button>
                  <button
                    onClick={() => setActiveTab('remove')}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      activeTab === 'remove'
                        ? 'bg-red-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    Remove Badges
                  </button>
                </div>
              </div>

              {/* Current Badges */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-white mb-3">Current Badges ({currentBadges.length})</h3>
                {currentBadges.length > 0 ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                    {currentBadges.map((badgeId) => {
                      const badge = getBadgeInfo(badgeId);
                      return badge ? (
                        <div
                          key={badgeId}
                          className="p-3 bg-gray-700 rounded-lg border border-gray-600 text-center"
                        >
                          <div className="text-2xl mb-1">{badge.icon}</div>
                          <div className="text-white text-sm font-medium">{badge.name}</div>
                          <div className="text-gray-400 text-xs">{badge.category}</div>
                        </div>
                      ) : null;
                    })}
                  </div>
                ) : (
                  <p className="text-gray-400">No badges awarded yet.</p>
                )}
              </div>

              {/* Available Badges */}
              <div className="mb-6">
                <h3 className="text-lg font-medium text-white mb-3">
                  {activeTab === 'award' ? 'Available Badges to Award' : 'Current Badges to Remove'}
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {availableBadges
                    .filter(badge => 
                      activeTab === 'award' 
                        ? !currentBadges.includes(badge.id)
                        : currentBadges.includes(badge.id)
                    )
                    .map((badge) => (
                      <div
                        key={badge.id}
                        onClick={() => toggleBadgeSelection(badge.id)}
                        className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                          selectedBadges.includes(badge.id)
                            ? 'border-green-500 bg-green-900/20'
                            : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                        }`}
                      >
                        <div className="text-2xl mb-1">{badge.icon}</div>
                        <div className="text-white text-sm font-medium">{badge.name}</div>
                        <div className="text-gray-400 text-xs">{badge.category}</div>
                        {selectedBadges.includes(badge.id) && (
                          <div className="text-green-400 text-xs mt-1">✓ Selected</div>
                        )}
                      </div>
                    ))}
                </div>
              </div>

              {/* Action Buttons */}
              {selectedBadges.length > 0 && (
                <div className="flex space-x-4">
                  <button
                    onClick={activeTab === 'award' ? handleAwardBadge : handleRemoveBadge}
                    disabled={actionLoading}
                    className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                      activeTab === 'award'
                        ? 'bg-green-600 hover:bg-green-700 text-white'
                        : 'bg-red-600 hover:bg-red-700 text-white'
                    } disabled:opacity-50`}
                  >
                    {actionLoading 
                      ? 'Processing...' 
                      : activeTab === 'award' 
                        ? `Award ${selectedBadges.length} Badge${selectedBadges.length > 1 ? 's' : ''}`
                        : `Remove ${selectedBadges.length} Badge${selectedBadges.length > 1 ? 's' : ''}`
                    }
                  </button>
                  <button
                    onClick={() => setSelectedBadges([])}
                    className="px-6 py-3 bg-gray-600 hover:bg-gray-500 text-white rounded-lg font-medium transition-colors"
                  >
                    Clear Selection
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Audit Log */}
          {selectedUser && auditLog.length > 0 && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-xl font-semibold text-white mb-4">Badge History</h2>
              <div className="space-y-3">
                {auditLog.slice(0, 10).map((entry) => {
                  const badge = getBadgeInfo(entry.badgeId);
                  return (
                    <div key={entry.id} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="text-2xl">{badge?.icon || '🏆'}</div>
                        <div>
                          <div className="text-white font-medium">
                            {badge?.name || entry.badgeId} {entry.action}
                          </div>
                          <div className="text-gray-400 text-sm">
                            {new Date(entry.date).toLocaleString()}
                          </div>
                        </div>
                      </div>
                      <div className={`px-3 py-1 rounded-full text-xs font-medium ${
                        entry.action === 'awarded' 
                          ? 'bg-green-900 text-green-300' 
                          : 'bg-red-900 text-red-300'
                      }`}>
                        {entry.action.toUpperCase()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Messages */}
          {message && (
            <div className="bg-green-900/20 border border-green-600 rounded-lg p-4 mb-6">
              <div className="text-green-400">{message}</div>
            </div>
          )}

          {error && (
            <div className="bg-red-900/20 border border-red-600 rounded-lg p-4 mb-6">
              <div className="text-red-400">{error}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminBadgeManagement; 